import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import PwaBanners from "@/components/PwaBanners";
import PayableAlert from "@/components/PayableAlert";
import {
  LayoutDashboard, ShoppingCart, Package, Boxes, ArrowDownCircle, ArrowUpCircle,
  BarChart3, Users, LogOut, Menu, X, Zap, UserCircle2, Truck, ShoppingBag,
  FileText, ClipboardCheck, Receipt, Settings as SettingsIcon, LayoutGrid, Calculator
} from "lucide-react";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, tid: "nav-dashboard-link" },
  { to: "/pdv", label: "PDV", icon: ShoppingCart, tid: "nav-pdv-link" },
  { to: "/vendas", label: "Vendas", icon: Receipt, tid: "nav-vendas-link" },
  { to: "/orcamentos", label: "Orçamentos", icon: FileText, tid: "nav-orcamentos-link" },
  { to: "/produtos", label: "Produtos & Serviços", icon: Package, tid: "nav-produtos-link" },
  { to: "/gestao-produtos", label: "Gestão de Produtos", icon: LayoutGrid, tid: "nav-gestao-produtos-link" },
  { to: "/precificacao", label: "Precificação", icon: Calculator, tid: "nav-precificacao-link", admin: true },
  { to: "/clientes", label: "Clientes", icon: UserCircle2, tid: "nav-clientes-link" },
  { to: "/fornecedores", label: "Fornecedores", icon: Truck, tid: "nav-fornecedores-link" },
  { to: "/compras", label: "Compras", icon: ShoppingBag, tid: "nav-compras-link" },
  { to: "/estoque", label: "Estoque", icon: Boxes, tid: "nav-estoque-link" },
  { to: "/inventario", label: "Inventário", icon: ClipboardCheck, tid: "nav-inventario-link" },
  { to: "/contas-a-pagar", label: "Contas a Pagar", icon: ArrowDownCircle, tid: "nav-pagar-link", admin: true },
  { to: "/contas-a-receber", label: "Contas a Receber", icon: ArrowUpCircle, tid: "nav-receber-link", admin: true },
  { to: "/relatorios", label: "Relatórios", icon: BarChart3, tid: "nav-relatorios-link", admin: true },
  { to: "/configuracoes", label: "Configurações", icon: SettingsIcon, tid: "nav-config-link", admin: true },
  { to: "/usuarios", label: "Usuários", icon: Users, tid: "nav-usuarios-link", admin: true },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const isAdmin = user?.role === "admin";
  const items = NAV.filter((n) => !n.admin || isAdmin);

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar - desktop */}
      <aside className="hidden md:flex md:w-64 flex-col bg-slate-900 text-white p-5 sticky top-0 h-screen">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center">
            <Zap className="w-5 h-5" />
          </div>
          <div>
            <div className="font-display font-bold text-lg leading-none">NEXUS</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400">PDV & ERP</div>
          </div>
        </div>
        <nav className="flex-1 space-y-1" data-testid="sidebar-nav">
          {items.map((it) => (
            <NavLink key={it.to} to={it.to} data-testid={it.tid}
              className={({ isActive }) => "side-link " + (isActive ? "active" : "")}>
              <it.icon className="w-4 h-4" /> {it.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-slate-800 pt-4 mt-4">
          <PwaBanners variant="sidebar" />
          <div className="text-xs text-slate-400">Logado como</div>
          <div className="font-semibold text-sm truncate" data-testid="user-name">{user?.name}</div>
          <div className="text-xs text-blue-400 uppercase tracking-wider mb-3">{user?.role}</div>
          <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn"
            className="w-full text-slate-200 hover:bg-slate-800 hover:text-white justify-start">
            <LogOut className="w-4 h-4 mr-2" /> Sair
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-slate-900 text-white flex items-center justify-between p-3">
        <button onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
        <div className="font-display font-bold">NEXUS PDV</div>
        <button onClick={handleLogout}><LogOut className="w-5 h-5" /></button>
      </div>

      {open && (
        <div className="md:hidden fixed inset-0 top-12 z-30 bg-slate-900 text-white p-4 space-y-1 overflow-y-auto">
          {items.map((it) => (
            <NavLink key={it.to} to={it.to} data-testid={"m-" + it.tid} onClick={() => setOpen(false)}
              className={({ isActive }) => "side-link " + (isActive ? "active" : "")}>
              <it.icon className="w-4 h-4" /> {it.label}
            </NavLink>
          ))}
        </div>
      )}

      <main className="flex-1 min-w-0 pt-16 md:pt-0 p-4 sm:p-6 lg:p-8 max-w-full">
        <PwaBanners variant="offline-banner" />
        <PayableAlert />
        <Outlet />
      </main>
    </div>
  );
}
